import { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    const saved = localStorage.getItem('cart');
    return saved ? JSON.parse(saved) : [];
  });
  
  const [orderDetails, setOrderDetails] = useState({
    customer_name: '',
    email: '',
    phone: '',
    order_type: 'collection',
    address: '',
    special_instructions: ''
  });

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (item) => {
    setCartItems(prev => {
      const existing = prev.find(i => i.menu_item_id === item.id);
      if (existing) {
        return prev.map(i => 
          i.menu_item_id === item.id 
            ? { ...i, quantity: i.quantity + 1 }
            : i
        );
      }
      return [...prev, {
        menu_item_id: item.id,
        name: item.name,
        price: item.price,
        quantity: 1,
        special_instructions: ''
      }];
    });
  };

  const removeFromCart = (menuItemId) => {
    setCartItems(prev => prev.filter(i => i.menu_item_id !== menuItemId));
  };

  const updateQuantity = (menuItemId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(menuItemId);
      return;
    }
    setCartItems(prev => 
      prev.map(i => 
        i.menu_item_id === menuItemId 
          ? { ...i, quantity }
          : i
      )
    );
  };

  const updateItemInstructions = (menuItemId, instructions) => {
    setCartItems(prev => 
      prev.map(i => 
        i.menu_item_id === menuItemId 
          ? { ...i, special_instructions: instructions }
          : i
      )
    );
  };

  const clearCart = () => {
    setCartItems([]);
    localStorage.removeItem('cart');
  };

  const getSubtotal = () => {
    return cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const getItemCount = () => {
    return cartItems.reduce((sum, item) => sum + item.quantity, 0);
  };

  return (
    <CartContext.Provider value={{
      cartItems,
      orderDetails,
      setOrderDetails,
      addToCart,
      removeFromCart,
      updateQuantity,
      updateItemInstructions,
      clearCart,
      getSubtotal,
      getItemCount
    }}>
      {children}
    </CartContext.Provider>
  );
};
